import React, { useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi, X } from 'lucide-react';
import { toast } from 'sonner';
import { getQueueCount } from '../utils/offlineQueue';

const timeAgo = (ts) => {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
};

// Slim connectivity banner for operators: amber while offline (with last-sync
// age when cached data is being shown + queued-action count), brief green
// flash on reconnect. Toasts when queued check-ins finish syncing.
export const OfflineBanner = () => {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [justBack, setJustBack] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [pending, setPending] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const backTimer = useRef(null);

  // Auto-collapse the offline pill after 8s so nobody stares at it; a fresh
  // offline event (or new cached-data signal) re-opens it.
  useEffect(() => {
    if (offline) {
      setCollapsed(false);
      const t = setTimeout(() => setCollapsed(true), 8000);
      return () => clearTimeout(t);
    }
    setCollapsed(false);
  }, [offline]);

  useEffect(() => {
    getQueueCount().then(setPending);
    const goOffline = () => { setOffline(true); setJustBack(false); };
    const goOnline = () => {
      setOffline(false);
      setCachedAt(null);
      setJustBack(true);
      clearTimeout(backTimer.current);
      backTimer.current = setTimeout(() => setJustBack(false), 3500);
    };
    const onCachedData = (e) => {
      setOffline(true);
      if (e.detail?.cachedAt) setCachedAt(e.detail.cachedAt);
    };
    const onQueued = (e) => setPending(e.detail?.pending ?? 0);
    const onSynced = (e) => {
      setPending(e.detail?.pending ?? 0);
      const n = e.detail?.synced || 0;
      if (n > 0) toast.success(`Synced ${n} offline check-in${n > 1 ? 's' : ''}`);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    window.addEventListener('bf-offline-data', onCachedData);
    window.addEventListener('bf-offline-queued', onQueued);
    window.addEventListener('bf-offline-synced', onSynced);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('bf-offline-data', onCachedData);
      window.removeEventListener('bf-offline-queued', onQueued);
      window.removeEventListener('bf-offline-synced', onSynced);
      clearTimeout(backTimer.current);
    };
  }, []);

  if (!offline && !justBack) return null;

  const showPill = offline ? !collapsed : justBack;

  // Minimal drop-toast pill (top center) instead of a full-width stripe.
  return (
    <div className="fixed inset-x-0 z-[9999] flex justify-center pointer-events-none px-4" style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}>
      <div
        data-testid="offline-banner"
        className={`pointer-events-auto flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 shadow-lg text-xs font-medium max-w-[92vw] transition-[transform,opacity] duration-500 ease-out text-white ${
          offline ? 'bg-amber-500' : 'bg-emerald-500'
        } ${showPill ? 'translate-y-0 opacity-100' : '-translate-y-24 opacity-0'}`}
      >
        {offline ? (
          <>
            <WifiOff className="w-3.5 h-3.5 shrink-0" />
            <span data-testid="offline-banner-text" className="truncate">
              You're offline{cachedAt ? ` — showing data last synced ${timeAgo(cachedAt)}` : ' — showing your last synced data'}
              {pending > 0 ? ` · ${pending} check-in${pending > 1 ? 's' : ''} queued` : ''}
            </span>
            <button
              onClick={() => setCollapsed(true)}
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors shrink-0"
              data-testid="offline-banner-dismiss"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <Wifi className="w-3.5 h-3.5 shrink-0" />
            <span className="pr-2">Back online{pending > 0 ? ` — syncing ${pending} check-in${pending > 1 ? 's' : ''}…` : ''}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineBanner;
